/**
 * localStorage utilities for Garagr Vue applications
 * Provides safe, centralized access to browser storage
 */

/**
 * Centralized storage key constants
 */
export const STORAGE_KEYS = {
  USER_LOCATION: 'garagr_user_location',
  GROUP_FILTER_SELECTIONS: 'garagr_group_filters'
};

/**
 * Safely get an item from localStorage with JSON parsing
 * @param {string} key - Storage key
 * @param {any} defaultValue - Default value if not found or parse error
 * @returns {any} Stored value or default
 * @example
 * const location = getStorageItem(STORAGE_KEYS.USER_LOCATION, null)
 */
export function getStorageItem(key, defaultValue = null) {
  try {
    const item = localStorage.getItem(key);
    if (item === null) return defaultValue;
    return JSON.parse(item);
  } catch (error) {
    console.error(`Error reading from localStorage key "${key}":`, error);
    return defaultValue;
  }
}

/**
 * Safely set an item in localStorage with JSON stringification
 * @param {string} key - Storage key
 * @param {any} value - Value to store
 * @returns {boolean} True if successful, false otherwise
 * @example
 * setStorageItem(STORAGE_KEYS.USER_LOCATION, { lat: 26.2034, lng: -98.2300 })
 */
export function setStorageItem(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (error) {
    console.error(`Error writing to localStorage key "${key}":`, error);
    return false;
  }
}

/**
 * Safely remove an item from localStorage
 * @param {string} key - Storage key
 * @returns {boolean} True if successful, false otherwise
 * @example
 * removeStorageItem(STORAGE_KEYS.USER_LOCATION)
 */
export function removeStorageItem(key) {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error(`Error removing localStorage key "${key}":`, error);
    return false;
  }
}

/**
 * Get stored user location data
 * @returns {Object|null} Location data with { lat, lng, timestamp, accuracy? } or null
 * @example
 * const location = getUserLocation()
 * if (location) {
 *   console.log(`User at ${location.lat}, ${location.lng}`)
 * }
 */
export function getUserLocation() {
  const location = getStorageItem(STORAGE_KEYS.USER_LOCATION, null);
  
  // Validate structure
  if (location && typeof location.lat === 'number' && typeof location.lng === 'number') {
    return location;
  }
  
  return null;
}

/**
 * Store user location with timestamp
 * @param {Object} locationData - Location data with lat, lng, and optional accuracy
 * @returns {boolean} True if successful
 * @example
 * setUserLocation({ lat: 26.2034, lng: -98.2300, accuracy: 50 })
 */
export function setUserLocation(locationData) {
  if (!locationData || typeof locationData.lat !== 'number' || typeof locationData.lng !== 'number') {
    console.error('Invalid location data structure');
    return false;
  }
  
  const data = {
    ...locationData,
    timestamp: new Date().toISOString()
  };
  
  return setStorageItem(STORAGE_KEYS.USER_LOCATION, data);
}

/**
 * Clear stored user location
 * @returns {boolean} True if successful
 * @example
 * clearUserLocation()
 */
export function clearUserLocation() {
  return removeStorageItem(STORAGE_KEYS.USER_LOCATION);
}

/**
 * Get stored filter selections for a specific app
 * @param {string} appKey - Unique key for the app (e.g., 'saved-listings', 'saved-listings-map')
 * @returns {Set|null} Set of selected group IDs or null
 * @example
 * const selections = getGroupFilterSelections('saved-listings')
 */
export function getGroupFilterSelections(appKey) {
  const allFilters = getStorageItem(STORAGE_KEYS.GROUP_FILTER_SELECTIONS, {});
  const selections = allFilters[appKey];
  
  if (Array.isArray(selections)) {
    return new Set(selections);
  }
  
  return null;
}

/**
 * Store filter selections for a specific app
 * @param {string} appKey - Unique key for the app
 * @param {Set} selections - Set of selected group IDs
 * @returns {boolean} True if successful
 * @example
 * const selections = new Set(['1', '2', '3'])
 * setGroupFilterSelections('saved-listings', selections)
 */
export function setGroupFilterSelections(appKey, selections) {
  const allFilters = getStorageItem(STORAGE_KEYS.GROUP_FILTER_SELECTIONS, {});
  allFilters[appKey] = Array.from(selections);
  return setStorageItem(STORAGE_KEYS.GROUP_FILTER_SELECTIONS, allFilters);
}
